import React, { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Sparkles, Check, Eye } from 'lucide-react';
import { getBrazilianStatus } from '../../../../../utils/detectBrazil';
import { footballDashboardPtBR } from '../../../../../components/mappings/sports-dash-mappings/football-dashboard.pt-br';
import { rtdbFootballService as FootballService } from '../../rtdb';
import { useAddToChat } from '../../../../../components/chat/chatContext';
import XPicksButton from '../../../../../components/dashboards/components/shared/XPicksButton';
import PredictButton from '../../../../../components/dashboards/components/shared/PredictButton';
import ViewOddsButton from '../../../../../components/dashboards/components/shared/ViewOddsButton';
import PredictionBox from '../../../../../components/dashboards/components/shared/PredictionBox';

const FootballMobileGameCard = memo(({
  fixture,
  onViewOdds,
  isSelected = false,
  isFrozen = false,
  onToggleSelection,
  onViewPrediction,
  prediction = null,
  showPredictionButton = true
}) => {
  // Internationalization
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (footballDashboardPtBR[text] || text) : text;
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction || !fixture?.teams) return;

    const message = `${t('Analyze this Football prediction:')}

${fixture.teams?.away?.name} @ ${fixture.teams?.home?.name}

${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
${t('Confidence Level:')} ${prediction.confidence}
${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}

${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!fixture) return null;

  const { teams, goals, fixture: fixtureInfo, league } = fixture;
  const isLive = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(fixtureInfo?.status?.short);
  const isFinished = ['FT', 'AET', 'PEN'].includes(fixtureInfo?.status?.short);
  const isUpcoming = !isLive && !isFinished;
  const status = fixtureInfo?.status?.short;

  const getStatusDisplay = () => {
    if (isLive) {
      if (fixtureInfo?.status?.elapsed) return `${fixtureInfo.status.elapsed}'`;
      if (status === 'HT') return 'HT';
      return t('LIVE');
    }
    if (isFinished) return t('FT');
    return t('NOT STARTED');
  };

  const statusDisplay = getStatusDisplay();
  const statusBadge = isLive
    ? 'bg-red-500 text-white'
    : isFinished
      ? 'bg-white/20 text-white'
      : 'bg-white/10 text-gray-300';

  const predictLabel = isFrozen
    ? 'COOLDOWN'
    : isSelected
      ? 'SELECTED'
      : isLive
        ? 'PREDICT LIVE'
        : 'PREDICT';
  const PredictIcon = isFrozen ? Clock : isSelected ? Check : Sparkles;

  const formatGameTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';
    if (fixtureInfo?.date) return FootballService.formatTime(fixtureInfo.date);
    return 'TBD';
  };

  const getScoreDisplay = (scoreValue) => (isFinished || isLive ? (scoreValue ?? 0) : '--');

  const renderTeamRow = (teamData, scoreValue, fallbackLabel) => (
    <div className="flex items-center gap-3">
      {teamData?.logo && (
        <img
          src={teamData.logo}
          alt={teamData.name || fallbackLabel}
          className="w-10 h-10 object-contain border border-white/10 rounded-sm bg-black/40 flex-shrink-0"
          loading="lazy"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-mono text-white tracking-tight leading-tight truncate">
          {teamData?.name || fallbackLabel}
        </p>
      </div>
      <span className="text-2xl font-black text-white ml-2 tabular-nums">
        {getScoreDisplay(scoreValue)}
      </span>
    </div>
  );

  const renderHeader = () => (
    <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-500">
      <div className="flex items-center gap-2 truncate">
        <span>{league?.country || league?.name || '—'}</span>
        {league?.flag && (
          <img
            src={league.flag}
            alt={league.country}
            className="h-3 w-5 object-cover rounded-sm"
            loading="lazy"
          />
        )}
      </div>
      <div className="flex items-center gap-2 truncate">
        <span>{league?.name}</span>
        {league?.logo && (
          <img
            src={league.logo}
            alt={league.name}
            className="h-4 w-4 object-contain"
            loading="lazy"
          />
        )}
      </div>
    </div>
  );

  const renderTimeRow = () => {
    if (isFinished || isLive) return null;
    return (
      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2 text-white/60 text-xs">
        <Clock size={12} />
        <span>{formatGameTime()}</span>
        {fixtureInfo?.venue?.name && (
          <>
            <span className="text-white/30">•</span>
            <MapPin size={11} />
            <span className="text-[11px] font-semibold truncate">
              {fixtureInfo.venue.name}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className={`relative border border-white/10 px-4 py-3 bg-white/[0.03] transition-all`}
    >
      {renderHeader()}

      <div className="mt-3 flex items-center justify-center text-[11px] font-mono uppercase tracking-widest text-gray-400">
        <span className={`px-2 py-0.5 rounded-full ${statusBadge}`}>{statusDisplay}</span>
      </div>

      <div className="flex items-start justify-between mt-4 gap-4">
        <div className="space-y-3 flex-1">
          <p className="text-right text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500">Goals</p>
          {renderTeamRow(teams?.home, goals?.home, 'Home Team')}
          {renderTeamRow(teams?.away, goals?.away, 'Away Team')}
        </div>
      </div>

      {renderTimeRow()}

      {/* Prediction Result */}
      {prediction && (
        <div className="pt-3">
          <PredictionBox
            prediction={prediction}
            onAnalyze={handleAnalyzeInChat}
            variant="mobile"
            t={t}
          />
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: league?.id,
                date: fixtureInfo?.date,
                time: formatGameTime(),
                fixtureId: fixtureInfo?.id
              }}
              sport="football"
              label="XPICKS"
              icon={Sparkles}
            />
          </div>
        )}

        {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={predictLabel}
              icon={PredictIcon}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(fixture);
                  if (onViewPrediction && !isSelected) {
                    onViewPrediction(fixture);
                  }
                }
              }}
            />
          </div>
        )}

        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              icon={Eye}
              onClick={() => onViewOdds(fixture)}
            />
          </div>
        )}
      </div>
    </div>
  );
});

FootballMobileGameCard.displayName = 'MobileFootballGameCard';

export default FootballMobileGameCard;
